import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

interface Node {
  id: string;
  size: number;
  type: "system" | "lock";
  name?: string; // Added name field for access points
  accessPointCount?: number; // Added access point count for systems
}

interface Link {
  source: string;
  target: string;
  weight: number;
  avgTime?: number;
}

interface GraphData {
  nodes: Node[];
  links: Link[];
}

interface NetworkGraphProps {
  data: GraphData;
  width: number;
  height: number;
  selectedSystemId?: string;
  systemColorMap?: { [key: string]: string };
  onNodeClick?: (nodeId: string) => void;
  onNodeDoubleClick?: (nodeId: string) => void;
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({
  data,
  width,
  height,
  selectedSystemId,
  systemColorMap = {},
  onNodeClick,
  onNodeDoubleClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!data || !data.nodes.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous render

    // Pastel color palette for better visual appeal
    const pastelColors = [
      "hsl(220, 60%, 75%)", // Pastel Blue
      "hsl(120, 50%, 75%)", // Pastel Green
      "hsl(0, 60%, 80%)", // Pastel Red
      "hsl(45, 70%, 80%)", // Pastel Yellow
      "hsl(270, 50%, 80%)", // Pastel Purple
      "hsl(340, 60%, 80%)", // Pastel Pink
      "hsl(180, 50%, 75%)", // Pastel Cyan
      "hsl(25, 60%, 80%)", // Pastel Orange
      "hsl(300, 50%, 80%)", // Pastel Magenta
      "hsl(60, 60%, 80%)", // Pastel Olive
      "hsl(200, 60%, 75%)", // Pastel Light Blue
      "hsl(140, 50%, 75%)", // Pastel Forest Green
      "hsl(15, 70%, 80%)", // Pastel Red Orange
      "hsl(280, 55%, 80%)", // Pastel Violet
      "hsl(320, 65%, 80%)", // Pastel Hot Pink
    ];

    // Update the color function to use pastel colors
    const getNodeColor = (d: Node) => {
      const systemId = d.type === "system" ? d.id : d.id.split("_")[0];

      // Create hash from system ID for consistent color assignment
      let hash = 0;
      for (let i = 0; i < systemId.length; i++) {
        const char = systemId.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }

      const paletteIndex = Math.abs(hash) % pastelColors.length;
      return pastelColors[paletteIndex];
    };

    // Create scales
    const radiusScale = d3
      .scaleSqrt()
      .domain([0, d3.max(data.nodes, (d) => d.size) || 1])
      .range([8, 32]);

    const linkWidthScale = d3
      .scaleLinear()
      .domain([1, d3.max(data.links, (d) => d.weight) || 1])
      .range([2, 8]);

    // Smart marker scaling - uses square root to reduce the scaling effect
    const markerScale = d3
      .scaleSqrt()
      .domain([1, d3.max(data.links, (d) => d.weight) || 1])
      .range([2.5, 4.5]); // Increased minimum from 2.0 to 2.5 for better visibility of small circles

    // Create main container for zoom/pan
    const container = svg.append("g");

    // Create zoom behavior
    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
      });

    svg.call(zoom as any);

    // Set initial zoom (for example, scale = 0.8, no pan)
    const cx = width / 2;
    const cy = height / 2;
    const initialScale = 0.5;
    const initialTransform = d3.zoomIdentity
      .translate(cx - cx * initialScale, cy - cy * initialScale)
      .scale(initialScale);

    svg.call((zoom as any).transform, initialTransform);

    // Create simulation with specified distances and forces
    const simulation = d3
      .forceSimulation(data.nodes as any)
      .force(
        "link",
        d3
          .forceLink(data.links)
          .id((d: any) => d.id)
          .distance(160)
      ) // Changed back to 160
      .force("charge", d3.forceManyBody().strength(-50)) // Changed from -600 to -50
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force(
        "collision",
        d3.forceCollide().radius((d: any) => radiusScale(d.size) + 10)
      );

    // Create arrow markers with smart scaling
    container
      .append("defs")
      .selectAll("marker")
      .data(data.links)
      .enter()
      .append("marker")
      .attr("id", (d: any, i: number) => `arrow-${i}`)
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 10) // Position arrow tip at the edge of target node
      .attr("refY", 0)
      .attr("markerWidth", (d: any) => markerScale(d.weight))
      .attr("markerHeight", (d: any) => markerScale(d.weight))
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#666");

    // Create start circle markers with smart scaling
    container
      .append("defs")
      .selectAll("marker.start-circle")
      .data(data.links)
      .enter()
      .append("marker")
      .attr("class", "start-circle")
      .attr("id", (d: any, i: number) => `start-circle-${i}`)
      .attr("viewBox", "-3 -3 6 6") // Smaller viewBox for better proportions
      .attr("refX", 0)
      .attr("refY", 0)
      .attr("markerWidth", (d: any) => markerScale(d.weight))
      .attr("markerHeight", (d: any) => markerScale(d.weight))
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", 2) // Fixed radius for consistent circle size
      .attr("fill", "#666");

    // Create links group (below nodes in visual hierarchy)
    const linksGroup = container.append("g").attr("class", "links");

    // Create links
    const link = linksGroup
      .selectAll("line")
      .data(data.links)
      .enter()
      .append("line")
      .attr("stroke", "#666")
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", (d: any) => linkWidthScale(d.weight))
      .attr("marker-start", (d: any, i: number) => `url(#start-circle-${i})`)
      .attr("marker-end", (d: any, i: number) => `url(#arrow-${i})`);

    // Create nodes group (above links in visual hierarchy)
    const nodesGroup = container.append("g").attr("class", "nodes");

    // Create nodes (removed white stroke)
    const node = nodesGroup
      .selectAll("circle")
      .data(data.nodes)
      .enter()
      .append("circle")
      .attr("r", (d: any) => radiusScale(d.size))
      .attr("fill", (d: any) => getNodeColor(d))
      .style("cursor", "pointer")
      .style("filter", "drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))")
      .call(
        d3
          .drag<SVGCircleElement, any>()
          .on("start", dragstarted)
          .on("drag", dragged)
          .on("end", dragended)
      );

    // Add labels (made bold)
    const labels = container
      .append("g")
      .attr("class", "labels")
      .selectAll("text")
      .data(data.nodes)
      .enter()
      .append("text")
      .text((d: any) => d.id)
      .attr("font-size", 11)
      .attr("fill", "#202124")
      .attr("text-anchor", "middle")
      .attr("dy", ".35em")
      .attr("font-family", "'Google Sans', sans-serif")
      .attr("font-weight", "bold") // Made bold
      .style("pointer-events", "none")
      .style("text-shadow", "0 1px 3px rgba(255, 255, 255, 0.8)");

    // Add tooltips with updated content based on graph type
    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("opacity", 0)
      .style("position", "absolute")
      .style("background", "rgba(255, 255, 255, 0.95)")
      .style("color", "#202124")
      .style("padding", "12px 16px")
      .style("border-radius", "8px")
      .style("font-size", "13px")
      .style("font-family", "'Google Sans', sans-serif")
      .style("pointer-events", "none")
      .style("border", "1px solid #e8eaed")
      .style("backdrop-filter", "blur(10px)")
      .style("box-shadow", "0 8px 32px rgba(0, 0, 0, 0.12)");

    node
      .on("mouseover", function (event: any, d: any) {
        d3.select(this as any)
          .transition()
          .duration(200)
          .attr("r", radiusScale(d.size) * 1.15)
          .style("filter", "drop-shadow(0 4px 16px rgba(26, 115, 232, 0.3))");

        tooltip.transition().duration(200).style("opacity", 1);

        // Different tooltip content based on node type
        let tooltipContent = "";
        if (d.type === "system") {
          // System ID Graph tooltip - use the access point count from server
          tooltipContent = `
            <div style="font-weight: 600; margin-bottom: 4px; color: #202124;">System ID: ${
              d.id
            }</div>
            <div style="color: #5f6368; font-size: 12px;">${
              d.name || "No name available"
            }</div>
            <div style="color: #5f6368; font-size: 12px;">Total Access Points: ${
              d.accessPointCount || 0
            }</div>
            <div style="color: #5f6368; font-size: 12px;">Total Unlocks: ${d.size.toLocaleString()}</div>
          `;
        } else {
          // Access Point Graph tooltip - show the name value from database
          tooltipContent = `
            <div style="font-weight: 600; margin-bottom: 4px; color: #202124;">Access Point: ${
              d.id
            }</div>
            <div style="color: #5f6368; font-size: 12px;">${
              d.name || "No name available"
            }</div>
            <div style="color: #5f6368; font-size: 12px; margin-top: 8px;">Total Unlocks: ${d.size.toLocaleString()}</div>
          `;
        }

        tooltip
          .html(tooltipContent)
          .style("left", event.pageX + 10 + "px")
          .style("top", event.pageY - 28 + "px");
      })
      .on("mouseout", function (event: any, d: any) {
        d3.select(this as any)
          .transition()
          .duration(200)
          .attr("r", radiusScale(d.size))
          .style("filter", "drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15))");

        tooltip.transition().duration(300).style("opacity", 0);
      })
      .on("click", function (event: any, d: any) {
        if (onNodeClick) {
          onNodeClick(d.id);
        }
      })
      .on("dblclick", function (event: any, d: any) {
        if (onNodeDoubleClick && d.type === "system") {
          onNodeDoubleClick(d.id);
        }
      });

    // Update positions on simulation tick with proper line positioning
    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => {
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const radius = radiusScale(d.source.size);
          return d.source.x + (dx * radius) / length;
        })
        .attr("y1", (d: any) => {
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const radius = radiusScale(d.source.size);
          return d.source.y + (dy * radius) / length;
        })
        .attr("x2", (d: any) => {
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const radius = radiusScale(d.target.size);
          // Position line end so arrow tip touches the rim perfectly
          return d.target.x - (dx * radius) / length;
        })
        .attr("y2", (d: any) => {
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const radius = radiusScale(d.target.size);
          // Position line end so arrow tip touches the rim perfectly
          return d.target.y - (dy * radius) / length;
        });

      node.attr("cx", (d: any) => d.x).attr("cy", (d: any) => d.y);

      labels.attr("x", (d: any) => d.x).attr("y", (d: any) => d.y);
    });

    // Drag functions
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Cleanup function
    return () => {
      tooltip.remove();
      simulation.stop();
    };
  }, [
    data,
    width,
    height,
    selectedSystemId,
    systemColorMap,
    onNodeClick,
    onNodeDoubleClick,
  ]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    />
  );
};

export default NetworkGraph;
